import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import MeetingRoom from '../components/MeetingRoom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Broker entry point — fetch the meeting (auth'd) to get the host_token,
 * then mount MeetingRoom in role="host".
 */
const BrokerMeetingPage = () => {
  const { meetingId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/meetings/${meetingId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(res.status === 404 ? 'Meeting not found' : 'Could not load meeting');
        const data = await res.json();
        if (cancelled) return;
        if (data.status === 'ended') {
          setError('This meeting has ended.');
        } else {
          setMeeting(data);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load meeting');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [meetingId, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-6">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-10 w-10 mx-auto text-rose-500 mb-3" />
          <h2 className="text-xl font-semibold mb-1">Can't open this meeting</h2>
          <p className="text-slate-400 text-sm mb-4">{error}</p>
          <Button onClick={() => navigate('/meetings')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Meetings
          </Button>
        </div>
      </div>
    );
  }
  return (
    <MeetingRoom
      roomId={meeting.room_id}
      token={meeting.host_token}
      role="host"
      hostName={meeting.host_name}
      clientName={meeting.client_name}
      proposalId={meeting.proposal_id}
      authToken={token}
      meetingId={meeting.id}
      onLeave={() => navigate('/meetings')}
    />
  );
};

export default BrokerMeetingPage;
